import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EmptyState } from 'Components/EmptyState'
import { AttendancePageShell } from 'Pages/Attendance/AttendanceComponents'
import { AttendanceSessionTable, cellKey } from 'Pages/Attendance/AttendanceSessionTable'
import { isIncompleteAttendanceCell } from 'Pages/Attendance/attendanceSessionFilters'
import {
  type AttendanceDateColumnDto,
  type AttendanceCellDto,
  type AttendanceNotInSessionDayClearDto,
  type AttendanceNotInSessionDayUpsertDto,
  type AttendanceRecordStatus,
  type AttendanceRecordsMutationResultDto,
  type AttendanceRosterRowDto,
  type AttendanceSessionRecordsDto
} from 'Pages/Attendance/types'
import { WeekPicker, weekStartDateValue } from 'Pages/Attendance/WeekPicker'
import * as AppRoutes from 'Pages/Routes'
import { useAttendanceAccountContext } from 'Pages/Attendance/useAttendanceAccountContext'
import { sendGet, sendPost, sendPut } from 'hooks/use-fetch'

interface AttendanceUpdate {
  row: AttendanceRosterRowDto
  cell: AttendanceCellDto
  status: AttendanceRecordStatus | null
  note?: string | null
}

type AttendanceClearUpdate = AttendanceUpdate & { status: null }
type AttendanceSaveUpdate = AttendanceUpdate & { status: AttendanceRecordStatus }
type AttendanceMutationBatch =
  | { kind: 'clear', updates: AttendanceClearUpdate[] }
  | { kind: 'save', updates: AttendanceSaveUpdate[] }

export function AttendanceSessionRoutePlaceholder (): JSX.Element {
  const accountContext = useAttendanceAccountContext()
  const location = useLocation()
  const navigate = useNavigate()
  const { sessionId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionIdValue = Number(sessionId)
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => initialWeekStart(searchParams.get('week')))
  const [sessionState, setSessionState] = useState<{ data?: AttendanceSessionRecordsDto, error: string[], loading: boolean }>(() => ({
    error: [],
    loading: accountContext.ready && !Number.isNaN(sessionIdValue)
  }))
  const [childNameFilter, setChildNameFilter] = useState(searchParams.get('student') ?? '')
  const [onlyIncomplete, setOnlyIncomplete] = useState(searchParams.get('onlyIncomplete') === 'true')
  const [savingEnrollmentIds, setSavingEnrollmentIds] = useState<Set<number>>(new Set())
  const [pendingLockedCompletionUpdates, setPendingLockedCompletionUpdates] = useState<AttendanceUpdate[] | null>(null)
  const confirmLockedCompletionButtonRef = useRef<HTMLButtonElement>(null)
  const activeSessionIdRef = useRef(sessionIdValue)

  useLayoutEffect(() => {
    if (activeSessionIdRef.current === sessionIdValue) {
      return
    }

    activeSessionIdRef.current = sessionIdValue
    setSelectedWeekStart(initialWeekStart(searchParams.get('week')))
    setSessionState({ error: [], loading: accountContext.ready && !Number.isNaN(sessionIdValue) })
    setChildNameFilter(searchParams.get('student') ?? '')
    setOnlyIncomplete(searchParams.get('onlyIncomplete') === 'true')
    setSavingEnrollmentIds(new Set())
    setPendingLockedCompletionUpdates(null)
  }, [accountContext.ready, searchParams, sessionIdValue])

  const updateSearch = (updates: { week?: string, student?: string, onlyIncomplete?: boolean }): void => {
    setSearchParams(currentParams => {
      const nextParams = new URLSearchParams(currentParams)

      if (updates.week !== undefined) {
        nextParams.set('week', updates.week)
      }

      if (updates.student !== undefined) {
        if (updates.student.trim() === '') {
          nextParams.delete('student')
        } else {
          nextParams.set('student', updates.student)
        }
      }

      if (updates.onlyIncomplete !== undefined) {
        if (updates.onlyIncomplete) {
          nextParams.set('onlyIncomplete', 'true')
        } else {
          nextParams.delete('onlyIncomplete')
        }
      }

      return nextParams
    }, { replace: true })
  }

  const setSelectedWeek = (weekStart: string): void => {
    setSelectedWeekStart(weekStart)
    updateSearch({ week: weekStart })
  }

  const loadRecords = useCallback(async (options?: { controller?: AbortController, showLoading?: boolean }): Promise<boolean> => {
    const from = selectedWeekStart
    const to = addDaysValue(selectedWeekStart, 4)

    if (options?.showLoading !== false) {
      setSessionState(current => ({ ...current, error: [], loading: true }))
    }

    const { response, success, error } = await sendGet(`/Attendance/Sessions/${sessionIdValue}/Records?from=${from}&to=${to}`, {}, options?.controller)

    if (options?.controller?.signal.aborted === true) {
      return false
    }

    if (!success) {
      setSessionState(current => ({
        data: current.data,
        error: error.filter(message => message.trim() !== '').length === 0 ? ['Attendance records could not be loaded.'] : error,
        loading: false
      }))
      return false
    }

    setSessionState({ data: response as AttendanceSessionRecordsDto, error: [], loading: false })
    return true
  }, [selectedWeekStart, sessionIdValue])

  useEffect(() => {
    if (!accountContext.ready || Number.isNaN(sessionIdValue)) {
      return
    }

    const controller = new AbortController()

    const runLoad = async (): Promise<void> => {
      await loadRecords({ controller })
    }

    void runLoad()
    return () => {
      controller.abort()
    }
  }, [accountContext.accountId, accountContext.accountType, accountContext.currentISDID, accountContext.ready, loadRecords, sessionIdValue])

  useEffect(() => {
    const clampedWeekStart = clampWeekStart(selectedWeekStart, sessionState.data?.totals?.startDate, sessionState.data?.totals?.endDate)
    if (!sameDate(clampedWeekStart, selectedWeekStart)) {
      setSelectedWeek(clampedWeekStart)
    }
  }, [selectedWeekStart, sessionState.data?.totals?.endDate, sessionState.data?.totals?.startDate])

  const rows = useMemo(() => {
    const loadedRows = sessionState.data?.rows ?? []
    return loadedRows.filter(row => {
      const childMatches = childNameFilter.trim() === '' || row.childName.toLocaleLowerCase().includes(childNameFilter.trim().toLocaleLowerCase())
      const incompleteMatches = !onlyIncomplete || row.cells.some(isIncompleteAttendanceCell)
      return childMatches && incompleteMatches
    })
  }, [childNameFilter, onlyIncomplete, sessionState.data?.rows])

  const savingCells = useMemo(() => {
    const cells = new Set<string>()
    for (const row of sessionState.data?.rows ?? []) {
      if (!savingEnrollmentIds.has(row.newEnrollmentId)) continue
      for (const date of sessionState.data?.dates ?? []) {
        cells.add(cellKey(row.newEnrollmentId, date.attendanceDate))
      }
    }
    return cells
  }, [savingEnrollmentIds, sessionState.data?.dates, sessionState.data?.rows])

  const setRows = (updater: (rows: AttendanceRosterRowDto[]) => AttendanceRosterRowDto[]): void => {
    setSessionState(current => current.data == null
      ? current
      : { ...current, data: { ...current.data, rows: updater(current.data.rows) } })
  }

  const requestSaveAttendance = (updates: AttendanceUpdate[]): boolean => {
    if (requiresLockedCompletionWarning(updates)) {
      setPendingLockedCompletionUpdates(updates)
      return false
    }

    void saveAttendance(updates)
    return true
  }

  const saveAttendance = async (updates: AttendanceUpdate[]): Promise<void> => {
    if (Number.isNaN(sessionIdValue) || updates.length === 0) return

    const mutationBatches = attendanceMutationBatches(updates)

    const affectedEnrollmentIds = new Set(updates.map(update => update.row.newEnrollmentId))
    let alreadySaving = false
    setSavingEnrollmentIds(current => {
      let hasSavingEnrollment = false
      affectedEnrollmentIds.forEach(id => {
        if (current.has(id)) {
          hasSavingEnrollment = true
        }
      })

      if (hasSavingEnrollment) {
        alreadySaving = true
        return current
      }

      const next = new Set(current)
      affectedEnrollmentIds.forEach(id => { next.add(id) })
      return next
    })

    if (alreadySaving) return

    setRows(rows => applyOptimisticUpdates(rows, updates))

    let saveErrorMessage: string | null = null
    try {
      for (const batch of mutationBatches) {
        const body = batch.kind === 'clear'
          ? { attendanceRecordIds: batch.updates.map(update => update.cell.attendanceRecordId).filter((id): id is number => id != null) }
          : {
              records: batch.updates.map(update => ({
                attendanceDate: update.cell.attendanceDate,
                newEnrollmentId: update.row.newEnrollmentId,
                status: update.status,
                ...(update.note === undefined ? {} : { note: update.note })
              }))
            }

        const request = batch.kind === 'clear'
          ? sendPost(`/Attendance/Sessions/${sessionIdValue}/Records/Clear`, body)
          : sendPut(`/Attendance/Sessions/${sessionIdValue}/Records`, body)
        const { response, success, error } = await request
        const mutationResult: AttendanceRecordsMutationResultDto | null = response

        if (!success || mutationResult == null || !mutationResult.success) {
          saveErrorMessage = mutationErrorMessage(error, mutationResult)
          break
        }
      }
    } finally {
      await loadRecords({ showLoading: false })

      setSavingEnrollmentIds(current => {
        const next = new Set(current)
        affectedEnrollmentIds.forEach(id => { next.delete(id) })
        return next
      })
    }

    if (saveErrorMessage != null) {
      toast.error(saveErrorMessage)
    }
  }

  const markNotInSessionDay = async (date: AttendanceDateColumnDto, reason: string): Promise<void> => {
    if (Number.isNaN(sessionIdValue)) return

    const body: AttendanceNotInSessionDayUpsertDto = {
      attendanceDate: date.attendanceDate,
      reason
    }
    const { response, success, error } = await sendPut(`/Attendance/Sessions/${sessionIdValue}/NotInSessionDays`, body)
    const mutationResult: AttendanceRecordsMutationResultDto | null = response
    await loadRecords({ showLoading: false })

    if (!success || mutationResult == null || !mutationResult.success) {
      toast.error(mutationErrorMessage(error, mutationResult))
    }
  }

  const clearNotInSessionDay = async (date: AttendanceDateColumnDto): Promise<void> => {
    if (Number.isNaN(sessionIdValue)) return

    const body: AttendanceNotInSessionDayClearDto = {
      attendanceDate: date.attendanceDate
    }
    const { response, success, error } = await sendPost(`/Attendance/Sessions/${sessionIdValue}/NotInSessionDays/Clear`, body)
    const mutationResult: AttendanceRecordsMutationResultDto | null = response
    await loadRecords({ showLoading: false })

    if (!success || mutationResult == null || !mutationResult.success) {
      toast.error(mutationErrorMessage(error, mutationResult))
    }
  }

  const moveWeek = (days: number): void => {
    setSelectedWeek(clampWeekStart(addDaysValue(selectedWeekStart, days), sessionState.data?.totals?.startDate, sessionState.data?.totals?.endDate))
  }

  const resetSessionFilters = (): void => {
    setChildNameFilter('')
    setOnlyIncomplete(false)
    updateSearch({ student: '', onlyIncomplete: false })
  }

  const confirmLockedCompletion = (): void => {
    const updates = pendingLockedCompletionUpdates
    setPendingLockedCompletionUpdates(null)
    if (updates != null) {
      void saveAttendance(updates)
    }
  }

  const todayDate = toDateValue(new Date())
  const todayWeekStart = weekStartDateValue(todayDate)
  const todayButtonDisabledReason = getTodayButtonDisabledReason(
    todayDate,
    selectedWeekStart,
    sessionState.data?.totals?.startDate,
    sessionState.data?.totals?.endDate
  )
  const todayButtonDisabled = todayButtonDisabledReason !== ''
  const selectedWeekIsFirstSessionWeek = isFirstSessionWeek(selectedWeekStart, sessionState.data?.totals?.startDate)
  const selectedWeekIsLastSessionWeek = isLastSessionWeek(selectedWeekStart, sessionState.data?.totals?.endDate)
  const hasSessionFilters = childNameFilter.trim() !== '' || onlyIncomplete
  const attendanceBackPath = attendanceSessionBackPath(location.state, searchParams.get('returnTo'))
  const attendanceBackLabel = attendanceBackPath === AppRoutes.ATTENDANCE ? 'Back to Attendance' : 'Back to Sessions'

  return (
    <AttendancePageShell
      title={sessionState.data?.sessionName ?? 'Session Attendance'}
      accountContext={accountContext}
      eyebrow={(
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '0.875rem' }} />}
          onClick={() => { navigate(attendanceBackPath) }}
          sx={{
            color: '#595959',
            fontFamily: 'Quicksand, sans-serif',
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          {attendanceBackLabel}
        </Button>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ alignItems: { xs: 'stretch', md: 'center' }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ alignItems: { xs: 'stretch', sm: 'center' }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Student"
              size="small"
              value={childNameFilter}
              onChange={event => {
                setChildNameFilter(event.target.value)
                updateSearch({ student: event.target.value })
              }}
              sx={{ minWidth: 220 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={onlyIncomplete}
                  onChange={event => {
                    setOnlyIncomplete(event.target.checked)
                    updateSearch({ onlyIncomplete: event.target.checked })
                  }}
                />
              )}
              label="Show Only Incomplete Attendance"
            />
          </Box>
          <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <WeekPicker
              label="Attendance Week"
              value={selectedWeekStart}
              minSelectableDate={sessionState.data?.totals?.startDate ?? undefined}
              maxSelectableDate={sessionState.data?.totals?.endDate ?? undefined}
              onChange={weekStart => {
                setSelectedWeek(clampWeekStart(weekStart, sessionState.data?.totals?.startDate, sessionState.data?.totals?.endDate))
              }}
            />
            <Tooltip title={todayButtonDisabledReason}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={todayButtonDisabled}
                  onClick={() => {
                    setSelectedWeek(clampWeekStart(todayWeekStart, sessionState.data?.totals?.startDate, sessionState.data?.totals?.endDate))
                  }}
                  sx={{
                    borderColor: '#B2B2B4',
                    borderRadius: '8px',
                    color: '#595959',
                    fontFamily: 'Quicksand, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    height: 36,
                    minWidth: 72,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#8F8F8F',
                      backgroundColor: '#F7F7F7'
                    }
                  }}
                >
                  Today
                </Button>
              </span>
            </Tooltip>
            <IconButton aria-label="Previous week" disabled={selectedWeekIsFirstSessionWeek} onClick={() => { moveWeek(-7) }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton aria-label="Next week" disabled={selectedWeekIsLastSessionWeek} onClick={() => { moveWeek(7) }}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {sessionState.loading && (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 4 }} role="status">
            <CircularProgress size={24} />
            <Typography>Loading session attendance.</Typography>
          </Box>
        )}

        {sessionState.error.length > 0 && (
          <Alert severity="error">
            {sessionState.error.join(' ')}
          </Alert>
        )}

        {!sessionState.loading && sessionState.error.length === 0 && sessionState.data != null && rows.length === 0 && (
          <EmptyState
            icon={<SearchOffIcon />}
            title="No attendance rows match the selected filters."
            description="Try a different week or clear the student and incomplete filters."
            actions={hasSessionFilters
              ? (
                <Button variant="outlined" onClick={resetSessionFilters}>
                  Reset filters
                </Button>
                )
              : undefined}
          />
        )}

        {!sessionState.loading && sessionState.error.length === 0 && sessionState.data != null && rows.length > 0 && (
          <AttendanceSessionTable
            dates={sessionState.data.dates}
            rows={rows}
            savingCells={savingCells}
            bulkActionsDisabled={savingEnrollmentIds.size > 0}
            onStatusChange={(row, cell, status, note) => { requestSaveAttendance([{ row, cell, status, note }]) }}
            onRecordChangesSave={(row, updates) => {
              return requestSaveAttendance(updates.map(update => ({
                row,
                cell: update.cell,
                status: update.status,
                note: update.note
              })))
            }}
            onClear={(row, cell) => { void saveAttendance([{ row, cell, status: null }]) }}
            onBulkStatusChange={(_date, status, cells) => { requestSaveAttendance(cells.map(({ row, cell }) => ({ row, cell, status }))) }}
            onBulkClear={(_date, cells) => { void saveAttendance(cells.map(({ row, cell }) => ({ row, cell, status: null }))) }}
            onMarkNotInSession={(date, reason) => { void markNotInSessionDay(date, reason) }}
            onClearNotInSession={(date) => { void clearNotInSessionDay(date) }}
          />
        )}
      </Box>
      <Dialog
        open={pendingLockedCompletionUpdates != null}
        onClose={() => { setPendingLockedCompletionUpdates(null) }}
        aria-labelledby="attendance-locked-completion-title"
        aria-describedby="attendance-locked-completion-description"
        TransitionProps={{
          onEntered: () => { confirmLockedCompletionButtonRef.current?.focus() }
        }}
      >
        <DialogTitle id="attendance-locked-completion-title">Complete older attendance?</DialogTitle>
        <DialogContent>
          <DialogContentText id="attendance-locked-completion-description">
            Once these attendance record(s) have been completed, you will not be able to change your responses.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPendingLockedCompletionUpdates(null) }}>Cancel</Button>
          <Button ref={confirmLockedCompletionButtonRef} variant="contained" onClick={confirmLockedCompletion}>Complete Records</Button>
        </DialogActions>
      </Dialog>
    </AttendancePageShell>
  )
}

function applyOptimisticUpdates (
  rows: AttendanceRosterRowDto[],
  updates: AttendanceUpdate[]
): AttendanceRosterRowDto[] {
  return rows.map(row => {
    const rowUpdates = updates.filter(update => update.row.newEnrollmentId === row.newEnrollmentId)
    if (rowUpdates.length === 0) return row

    return {
      ...row,
      cells: row.cells.map(cell => {
        const update = rowUpdates.find(candidate => sameDate(candidate.cell.attendanceDate, cell.attendanceDate))
        if (update == null) return cell

        return {
          ...cell,
          attendanceRecordId: update.status == null ? null : cell.attendanceRecordId,
          cellState: update.status == null ? 'Empty' : 'Recorded',
          isMissing: update.status == null,
          note: update.note === undefined ? cell.note : update.note,
          status: update.status
        }
      })
    }
  })
}

function attendanceMutationBatches (updates: AttendanceUpdate[]): AttendanceMutationBatch[] {
  const clearUpdates = updates.filter((update): update is AttendanceClearUpdate => update.status == null)
  const saveUpdates = updates.filter((update): update is AttendanceSaveUpdate => update.status != null)
  return [
    clearUpdates.length === 0 ? null : { kind: 'clear' as const, updates: clearUpdates },
    saveUpdates.length === 0 ? null : { kind: 'save' as const, updates: saveUpdates }
  ].filter((batch): batch is AttendanceMutationBatch => batch != null)
}

function requiresLockedCompletionWarning (updates: AttendanceUpdate[]): boolean {
  return updates.some(update => update.status != null && update.cell.willLockAfterSubmit === true)
}

function attendanceSessionBackPath (state: unknown, returnTo: string | null): string {
  if (
    state != null &&
    typeof state === 'object' &&
    'attendanceBackPath' in state &&
    typeof state.attendanceBackPath === 'string' &&
    isAttendanceBackPath(state.attendanceBackPath)
  ) {
    return state.attendanceBackPath
  }

  if (returnTo != null && isAttendanceBackPath(returnTo)) {
    return returnTo
  }

  return AppRoutes.ATTENDANCE
}

function isAttendanceBackPath (path: string): boolean {
  return path.startsWith(AppRoutes.ATTENDANCE) && !path.startsWith('/Attendance/Session/')
}

function mutationErrorMessage (transportErrors: string[], result: AttendanceRecordsMutationResultDto | null): string {
  const resultErrors = result?.errors?.map(error => error.error).filter(error => error.trim() !== '') ?? []
  const errors = resultErrors.length > 0 ? resultErrors : transportErrors.filter(error => error.trim() !== '')
  return errors.length === 0 ? 'Attendance could not be saved.' : errors.join(' ')
}

function initialWeekStart (weekParam: string | null): string {
  if (weekParam != null && /^\d{4}-\d{2}-\d{2}$/.test(weekParam)) {
    return weekStartDateValue(weekParam)
  }

  return weekStartDateValue(toDateValue(new Date()))
}

function sameDate (left: string, right: string): boolean {
  return left.substring(0, 10) === right.substring(0, 10)
}

function getTodayButtonDisabledReason (todayDate: string, selectedWeekStart: string, sessionStart?: string | null, sessionEnd?: string | null): string {
  if (sessionStart != null && compareDateValues(todayDate, sessionStart) < 0) {
    return 'Today is before this session starts.'
  }

  if (sessionEnd != null && compareDateValues(todayDate, sessionEnd) > 0) {
    return 'Today is after this session ends.'
  }

  if (sameDate(selectedWeekStart, clampWeekStart(weekStartDateValue(todayDate), sessionStart, sessionEnd))) {
    return 'Already viewing today.'
  }

  return ''
}

function compareDateValues (left: string, right: string): number {
  return left.substring(0, 10).localeCompare(right.substring(0, 10))
}

function clampWeekStart (weekStart: string, sessionStart?: string | null, sessionEnd?: string | null): string {
  if (sessionStart == null && sessionEnd == null) return weekStart

  const minWeekStart = sessionStart == null ? undefined : weekStartDateValue(sessionStart)
  const maxWeekStart = sessionEnd == null ? undefined : weekStartDateValue(sessionEnd)
  const candidate = weekStartDateValue(weekStart)

  if (minWeekStart != null && candidate < minWeekStart) {
    return minWeekStart
  }

  if (maxWeekStart != null && candidate > maxWeekStart) {
    return maxWeekStart
  }

  return candidate
}

function isFirstSessionWeek (selectedWeekStart: string, sessionStart?: string | null): boolean {
  return sessionStart != null && sameDate(selectedWeekStart, weekStartDateValue(sessionStart))
}

function isLastSessionWeek (selectedWeekStart: string, sessionEnd?: string | null): boolean {
  return sessionEnd != null && sameDate(selectedWeekStart, weekStartDateValue(sessionEnd))
}

function addDaysValue (value: string, days: number): string {
  const date = parseDateOnly(value)
  date.setDate(date.getDate() + days)
  return toDateValue(date)
}

function parseDateOnly (value: string): Date {
  const [year, month, day] = value.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toDateValue (date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
